import {
  hasRegFlowDeepLinkPending,
  isAvailChartsLockHash,
  isLocationDetailPathname,
  isRegFlowHash,
} from '~/utils/location-av-chart-deeplink'

const shouldSkipPulseHashPrefetch = () => {
  if (hasRegFlowDeepLinkPending()) return true
  if (isRegFlowHash(window.location.hash)) return true
  if (
    isLocationDetailPathname(window.location.pathname)
    && isAvailChartsLockHash(window.location.hash)
  ) {
    return true
  }
  return false
}

const PREFETCH_REGISTRY_KEY = '__pulsePrefetchedRoutes'
const PREFETCH_INFLIGHT_KEY = '__pulsePrefetchInFlight'
const PREFETCH_STATUS_KEY = '__pulsePrefetchRouteStatus'

// Normalize all incoming routes to a stable `/path` format for dedupe.
const normalizeRoute = (routePath = '') => {
  const trimmed = String(routePath || '').trim()
  if (!trimmed) return ''
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
}

// Store prefetched routes on `window` so multiple components can share state.
const ensureRegistry = () => {
  if (!import.meta.client) return new Set()
  if (!window[PREFETCH_REGISTRY_KEY]) {
    window[PREFETCH_REGISTRY_KEY] = new Set()
  }
  return window[PREFETCH_REGISTRY_KEY]
}

// Track active prefetch promises per route to avoid duplicate work.
const ensureInFlightRegistry = () => {
  if (!import.meta.client) return new Map()
  if (!window[PREFETCH_INFLIGHT_KEY]) {
    window[PREFETCH_INFLIGHT_KEY] = new Map()
  }
  return window[PREFETCH_INFLIGHT_KEY]
}

// Track route-level prefetch state so other composables can react safely.
const ensureStatusRegistry = () => {
  if (!import.meta.client) return new Map()
  if (!window[PREFETCH_STATUS_KEY]) {
    window[PREFETCH_STATUS_KEY] = new Map()
  }
  return window[PREFETCH_STATUS_KEY]
}

const setRouteStatus = (routePath, status) => {
  if (!import.meta.client) return
  const normalizedRoute = normalizeRoute(routePath)
  if (!normalizedRoute) return
  const statusRegistry = ensureStatusRegistry()
  statusRegistry.set(normalizedRoute, {
    status,
    updatedAt: Date.now()
  })
}

const getRouteStatus = (routePath) => {
  if (!import.meta.client) return { status: 'idle', updatedAt: 0 }
  const normalizedRoute = normalizeRoute(routePath)
  if (!normalizedRoute) return { status: 'idle', updatedAt: 0 }

  const statusRegistry = ensureStatusRegistry()
  const exactMatch = statusRegistry.get(normalizedRoute)
  if (exactMatch) return exactMatch

  return { status: 'idle', updatedAt: 0 }
}

// Wait until the Pulse app exposes `refreshRoute`, with a bounded timeout.
const waitForPulseRefreshRoute = (timeoutMs = 12000, pollMs = 150) => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now()

    const check = () => {
      const refreshRoute = window?.Pulse?.app?.refreshRoute
      if (typeof refreshRoute === 'function') {
        resolve(refreshRoute)
        return
      }

      if (Date.now() - startTime >= timeoutMs) {
        reject(new Error('Pulse app is not ready for prefetch.'))
        return
      }

      window.setTimeout(check, pollMs)
    }

    check()
  })
}

export const usePulsePrefetch = () => {
  const triggerPulseRouteRefresh = async (normalizedRoute) => {
    // Preserve current URL; we temporarily swap hash so Pulse refreshes this route.
    const originalUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
    const prefetchHashUrl = `${window.location.pathname}${window.location.search}#${normalizedRoute}`

    await waitForPulseRefreshRoute()

    const canReplaceHistory = typeof window?.history?.replaceState === 'function'
    const refreshRoute = window?.Pulse?.app?.refreshRoute
    if (typeof refreshRoute !== 'function') {
      throw new Error('Pulse refreshRoute function is not available.')
    }

    try {
      if (canReplaceHistory) {
        window.history.replaceState(window.history.state, '', prefetchHashUrl)
      }
      refreshRoute()
    } finally {
      // Always restore the original URL, even when refresh fails.
      if (canReplaceHistory) {
        window.history.replaceState(window.history.state, '', originalUrl)
      }
    }
  }

  const prefetchPulseRoute = async (routePath) => {
    if (!import.meta.client) return false

    const normalizedRoute = normalizeRoute(routePath)
    if (!normalizedRoute) return false

    // Hash swap + refreshRoute hijacks Pulse on #reg-flow deep links (→ choose-locations).
    if (shouldSkipPulseHashPrefetch()) {
      const prefetchedRoutes = ensureRegistry()
      prefetchedRoutes.add(normalizedRoute)
      setRouteStatus(normalizedRoute, 'success')
      return true
    }

    // Avoid repeating the same prefetch across sections/components.
    const prefetchedRoutes = ensureRegistry()
    if (prefetchedRoutes.has(normalizedRoute)) {
      setRouteStatus(normalizedRoute, 'success')
      return true
    }

    const inFlightRegistry = ensureInFlightRegistry()
    if (inFlightRegistry.has(normalizedRoute)) {
      return inFlightRegistry.get(normalizedRoute)
    }

    setRouteStatus(normalizedRoute, 'pending')

    const prefetchPromise = (async () => {
      try {
        await triggerPulseRouteRefresh(normalizedRoute)
        prefetchedRoutes.add(normalizedRoute)
        setRouteStatus(normalizedRoute, 'success')
        return true
      } catch {
        // Prefetch is best-effort only: failures should not affect page behavior.
        setRouteStatus(normalizedRoute, 'failed')
        return false
      } finally {
        inFlightRegistry.delete(normalizedRoute)
      }
    })()

    inFlightRegistry.set(normalizedRoute, prefetchPromise)

    try {
      return await prefetchPromise
    } catch {
      return false
    }
  }

  return {
    prefetchPulseRoute,
    getPulseRouteStatus: getRouteStatus
  }
}
